// iOS Safari only lets an AudioContext start inside a user gesture. The check-in
// page's "Start my consult" tap (or the call page's Rejoin tap) is the last
// gesture before the call, so both call contexts are created and resumed here,
// synchronously inside that tap, and the mic and player adopt them when the call
// starts. Ported from ottos-consult src/consult/callAudioPrime.js (PORTED-FROM.md).

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

let playbackContext: AudioContext | null = null;
let captureContext: AudioContext | null = null;

export function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function usable(context: AudioContext | null): AudioContext | null {
  return context && context.state !== "closed" ? context : null;
}

function wake(context: AudioContext) {
  try {
    void context.resume?.().catch(() => undefined);
    // A one-sample silent buffer is the long-standing iOS unlock; it must start in the tap.
    const buffer = context.createBuffer(1, 1, context.sampleRate);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    source.start(0);
  } catch {
    // The call resumes the contexts again when it starts; if that fails too the
    // call shows "Tap to unmute Otto", so this is not a silent failure.
  }
}

/** Call synchronously at the top of the click handler, before any await. */
export function primeCallAudio(): boolean {
  const Ctor = audioContextCtor();
  if (!Ctor) return false;
  try {
    playbackContext = usable(playbackContext) ?? new Ctor();
    captureContext = usable(captureContext) ?? new Ctor();
  } catch {
    return false;
  }
  wake(playbackContext);
  wake(captureContext);
  return true;
}

export function takePrimedPlaybackContext(): AudioContext | null {
  const context = usable(playbackContext);
  playbackContext = null;
  return context;
}

export function takePrimedCaptureContext(): AudioContext | null {
  const context = usable(captureContext);
  captureContext = null;
  return context;
}

export function releasePrimedCallAudio(): void {
  void playbackContext?.close?.().catch(() => undefined);
  void captureContext?.close?.().catch(() => undefined);
  playbackContext = null;
  captureContext = null;
}
